// Instant money figures, honestly.
//
// THE PRINCIPLE. A number on a money surface is either a real measurement for
// the wallet on screen, or it is visibly not a number. It is never a
// placeholder that reads as data.
//
// `0` is the worst placeholder: a wallet holding 100 GRAM painted as `0` is a
// statement the user acts on. `—` that turns into a number a beat later is the
// same lie told twice — first "we have no balance", then "actually we did".
// Both were on screen at every page load, because the last-known figures lived
// in module memory and module memory dies with the tab.
//
// So the rule for every balance, holding and total:
//   1. paint the last real measurement for THIS wallet, immediately, from
//      storage — before any request has finished;
//   2. replace it the moment a fresh measurement lands;
//   3. when nothing was ever measured for this wallet, render a skeleton —
//      not `0`, not `—`.
//
// Values are scoped by wallet. A figure remembered for one wallet must never
// paint under another: that is the same lie with a different subject.

const KEY = 'wils.lastknown.v1'
// Enough for a wallet list plus the figures each surface remembers; oldest
// entries are dropped so a long-lived browser cannot grow this without bound.
const MAX_ENTRIES = 64

type Store = Record<string, string>

/** Namespaced, wallet-scoped key. An empty wallet address yields null: an
 *  unscoped figure is exactly what must not be remembered. */
export function figureKey(scope: string, walletAddress: string | null | undefined): string | null {
  const addr = walletAddress?.trim()
  if (!addr) return null
  return `${scope}|${addr}`
}

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Store = {}
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function write(store: Store): void {
  try {
    const entries = Object.entries(store)
    const kept = entries.slice(Math.max(0, entries.length - MAX_ENTRIES))
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(kept)))
  } catch {
    // storage unavailable — the figures stay in module memory for this tab
  }
}

export function readLastKnownFigure(key: string | null): string | null {
  if (!key) return null
  const value = read()[key]
  return value && value.length > 0 ? value : null
}

export function writeLastKnownFigure(key: string | null, value: string): void {
  if (!key || !value) return
  const store = read()
  if (store[key] === value) return
  // Re-insert at the end so the most recently seen wallets survive the cap.
  delete store[key]
  store[key] = value
  write(store)
}

export function resetLastKnownFiguresForTest(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // storage unavailable
  }
}
